import { store } from '@/store/store'
import {
	applyCrashBetCancelled,
	applyCrashBetPlaced,
	applyCrashCashOut,
	applyCrashRoundCrashed,
	applyCrashRoundFlying,
	applyCrashRoundWaiting,
	selectCrashGame,
	setCrashElapsedSeconds,
	setCrashHistory,
	setCrashGameId,
	setCrashMultiplier,
	setCrashRoundPlayers,
	setCrashRoundStats,
	setCrashScheduledBet,
	setCrashSubmitting,
	upsertCrashRoundPlayer,
} from '@/store/slices/crashSlice'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { getCrashSocket, disconnectCrashSocket } from '@/store/socket/crash-socket'
import { originalApi } from '@/store/api/originalApi'
import { paymentApi } from '@/store/api/paymentApi'
import { showApiErrorToast, showApiSuccessToast } from '@/lib/api-toast'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import type { CrashCancelBetRequest, CrashCashOutRequest, CrashPlaceBetRequest, CrashRoundPlayer, CrashSocketData, CrashSocketPlayer, CrashSocketResponse } from '@/types/crash'

function getEventData(payload: unknown): CrashSocketData {
	return (payload as CrashSocketResponse).data ?? {}
}

function mapDisplayPlayers(players: CrashSocketPlayer[] | undefined): CrashRoundPlayer[] {
	if (!players?.length) {
		return []
	}

	return players.map((row) => ({
		playerId: row.player_id,
		username: row.username,
		betAmount: row.amount,
		currencyCode: row.currency_code,
		cashoutMultiplier: row.cashout_multiplier,
	}))
}

function applyBetUpdate(data: CrashSocketData) {
	store.dispatch(setCrashRoundPlayers(mapDisplayPlayers(data.players)))
	store.dispatch(
		setCrashRoundStats({
			playerCount: data.player_count,
			totalBetAmount: data.total_bet_amount,
		}),
	)
}

function applyPlayerCashoutBroadcast(data: CrashSocketData) {
	if (!data.player_id) {
		return
	}

	store.dispatch(
		upsertCrashRoundPlayer({
			playerId: data.player_id,
			username: data.username ?? 'Player',
			betAmount: data.amount ?? 0,
			currencyCode: data.currency_code,
			cashoutMultiplier: data.cashout_multiplier ?? null,
		}),
	)
}

export class GameController {
	private flightStarted = false
	private zeroSeen = false
	private disposed = false
	private lastIsAuthenticated = this.authenticated
	private unsubscribeAuth: (() => void) | null = null

	constructor() {
		this.initSocket()
		this.subscribeToAuth()
		void this.loadHistory()
	}

	dispose() {
		this.disposed = true
		this.unsubscribeAuth?.()
		this.unsubscribeAuth = null
		const socket = getCrashSocket()
		socket.offAny(this.handleSocketEvent)
	}

	private get crashState() {
		return selectCrashGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private requireAuth(message: string): boolean {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private async loadHistory() {
		try {
			const response = await store.dispatch(originalApi.endpoints.getCrashRoundHistory.initiate({ page: 1, per_page: 20 }, { forceRefetch: true })).unwrap()

			if (this.disposed) {
				return
			}

			const history = (response.data ?? []).map((row) => row.crash_point).reverse()
			store.dispatch(setCrashHistory(history))
		} catch {
			// History is optional for first paint; gameplay still works.
		}
	}

	private initSocket() {
		const socket = getCrashSocket()
		socket.offAny(this.handleSocketEvent)
		socket.onAny(this.handleSocketEvent)

		if (!socket.connected) {
			socket.connect()
		}
	}

	private subscribeToAuth() {
		this.unsubscribeAuth = store.subscribe(() => {
			if (this.disposed) {
				return
			}

			const isAuthenticated = this.authenticated
			if (isAuthenticated === this.lastIsAuthenticated) {
				return
			}

			this.lastIsAuthenticated = isAuthenticated
			this.reconnectSocket()
		})
	}

	private reconnectSocket() {
		disconnectCrashSocket()
		this.initSocket()
	}

	private handleSocketEvent = (event: string, payload?: unknown) => {
		if (this.disposed) {
			return
		}

		const data = getEventData(payload)

		switch (event) {
			case 'roundStarted':
				this.handleRoundStarted(data)
				return
			case 'roundBettingOnHold':
				this.handleRoundBettingOnHold(data)
				return
			case 'progressTimer':
				this.handleProgressTimer(data)
				return
			case 'roundStopped':
				this.handleRoundStopped(data)
				return
			case 'betUpdate':
				applyBetUpdate(data)
				return
			case 'playerCashout':
				applyPlayerCashoutBroadcast(data)
				return
			case 'autoCashout':
				this.applyAutoCashOut(data)
				return
			default:
				return
		}
	}

	private handleRoundStarted(data: CrashSocketData) {
		this.flightStarted = false
		this.zeroSeen = false
		store.dispatch(
			applyCrashRoundWaiting({
				crashGameId: data.crash_game_id ?? null,
				bettingWindowSeconds: data.betting_window_seconds ?? 5,
			}),
		)

		applyBetUpdate(data)
		void this.maybeAutoPlaceBet()
	}

	private handleRoundBettingOnHold(data: CrashSocketData) {
		store.dispatch(setCrashGameId(data.crash_game_id ?? null))

		this.startFlying()
	}

	private handleProgressTimer(data: CrashSocketData) {
		store.dispatch(setCrashGameId(data.crash_game_id ?? null))

		if (data.running_status) {
			const tenths = data.second_tenths ?? 0

			if (tenths === 0) {
				if (this.zeroSeen) {
					return
				}
				this.zeroSeen = true
			}

			if (!this.flightStarted) {
				this.startFlying()
			}

			store.dispatch(setCrashMultiplier(data.multiplier ?? 1))
			store.dispatch(setCrashElapsedSeconds((data.elapsed_ms ?? 0) / 1000))
			return
		}
		const crashPoint = data.crash_point
		this.handleCrash(crashPoint)
	}

	private handleRoundStopped(data: CrashSocketData) {
		store.dispatch(setCrashGameId(data.crash_game_id ?? null))

		const crashPoint = data.crash_point
		this.handleCrash(crashPoint)
	}

	private startFlying() {
		this.flightStarted = true
		store.dispatch(applyCrashRoundFlying())
	}

	private handleCrash(crashPoint: number) {
		const crash = this.crashState

		if (crash.phase === 'crashed' && crash.crashPoint === crashPoint) {
			return
		}

		this.flightStarted = false
		this.zeroSeen = false
		store.dispatch(applyCrashRoundCrashed(crashPoint))
	}

	private applyAutoCashOut(data: CrashSocketData) {
		const crash = this.crashState

		if (crash.hasCashedOut) {
			return
		}

		const cashoutMultiplier = data.cashout_multiplier
		const winAmount = data.cashout

		store.dispatch(applyCrashCashOut({ winAmount, cashoutMultiplier }))
		showApiSuccessToast(`Cashed out at ${cashoutMultiplier.toFixed(2)}x`)
	}

	private applyManualCashOut(data: CrashSocketData) {
		const crash = this.crashState

		if (crash.hasCashedOut) {
			return
		}

		const cashoutMultiplier = data.cashout_multiplier
		const winAmount = data.cashout

		store.dispatch(applyCrashCashOut({ winAmount, cashoutMultiplier }))
		showApiSuccessToast(`Cashed out at ${cashoutMultiplier.toFixed(2)}x`)
	}

	private async maybeAutoPlaceBet() {
		if (!this.authenticated) {
			return
		}

		const crash = this.crashState

		if ((crash.autoBetEnabled || crash.scheduledBet) && !crash.hasBet) {
			await this.placeBet()
		}
	}

	async placeBet() {
		if (!this.requireAuth('Please login to place a bet')) {
			return
		}

		const crash = this.crashState

		if (crash.phase !== 'waiting') {
			if (!crash.hasBet && !crash.scheduledBet) {
				store.dispatch(setCrashScheduledBet(true))
			}
			return
		}

		if (crash.hasBet || crash.isSubmitting || !crash.crashGameId) {
			return
		}

		const betAmount = Number(crash.betAmount)
		if (!Number.isFinite(betAmount) || betAmount <= 0) {
			showApiErrorToast('Enter a valid bet amount.')
			return
		}

		store.dispatch(setCrashSubmitting(true))

		const payload: CrashPlaceBetRequest = {
			crash_game_id: crash.crashGameId,
			currency_code: crash.currencyCode,
			amount: betAmount,
			idempotency_key: createIdempotencyKey(),
		}

		if (crash.autoCashoutEnabled) {
			payload.cashout_multiplier = Number(crash.autoCashoutMultiplier)
		}

		try {
			const response = await this.emitAck('placeBet', payload)
			const data = getEventData(response)
			store.dispatch(applyCrashBetPlaced({ betAmount: data.amount }))
			applyBetUpdate(data)
		} catch (error) {
			store.dispatch(setCrashSubmitting(false))
			store.dispatch(setCrashScheduledBet(false))
			const message = error instanceof Error ? error.message : 'Unable to place bet.'
			showApiErrorToast(message)
		}
	}

	async cancelBet() {
		if (!this.requireAuth('Please login to cancel a bet')) {
			return
		}

		const crash = this.crashState

		if (crash.scheduledBet && crash.phase !== 'waiting') {
			store.dispatch(setCrashScheduledBet(false))
			return
		}

		if (crash.phase !== 'waiting' || !crash.hasBet || crash.isSubmitting || !crash.crashGameId) {
			return
		}

		store.dispatch(setCrashSubmitting(true))

		const payload: CrashCancelBetRequest = {
			crash_game_id: crash.crashGameId,
		}

		try {
			const response = await this.emitAck('cancelBet', payload)
			store.dispatch(applyCrashBetCancelled())
			applyBetUpdate(getEventData(response))
		} catch (error) {
			store.dispatch(setCrashSubmitting(false))
			const message = error instanceof Error ? error.message : 'Unable to cancel bet.'
			showApiErrorToast(message)
		}
	}

	async cashOut() {
		if (!this.requireAuth('Please login to cash out')) {
			return
		}

		const crash = this.crashState

		if (crash.autoCashoutEnabled) {
			return
		}

		if (crash.phase !== 'flying' || !crash.hasBet || crash.hasCashedOut || crash.isSubmitting || !crash.crashGameId) {
			return
		}

		store.dispatch(setCrashSubmitting(true))

		const payload: CrashCashOutRequest = {
			crash_game_id: crash.crashGameId,
			cashout_multiplier: this.crashState.currentMultiplier,
		}

		try {
			const response = await this.emitAck('cashOut', payload)
			this.applyManualCashOut(getEventData(response))
		} catch (error) {
			store.dispatch(setCrashSubmitting(false))
			const message = error instanceof Error ? error.message : 'Unable to cash out.'
			showApiErrorToast(message)
		}
	}

	private emitAck<T extends CrashPlaceBetRequest | CrashCancelBetRequest | CrashCashOutRequest>(event: string, payload: T): Promise<CrashSocketResponse> {
		const socket = getCrashSocket()

		return new Promise((resolve, reject) => {
			socket.timeout(10000).emit(event, payload, (err: Error | null, response?: CrashSocketResponse) => {
				if (err) {
					reject(err)
					return
				}

				if (!response?.success) {
					reject(new Error(response?.error ?? response?.message ?? `${event} failed`))
					return
				}

				resolve(response)
			})
		})
	}
}
